import React from 'react';
import {
  FormControl,
  FormControlLabel,
  FormControlLabelProps,
  FormControlProps,
  FormGroup,
  FormGroupProps,
  FormHelperText,
  FormHelperTextProps,
  FormLabel,
  FormLabelProps,
} from '@mui/material';
import { Field, useField } from 'react-final-form';

import { useCanEditFormFieldsContext } from '@occc-app/App/CanEditFormFieldsContext';

import { hasError } from './utils';

import { Checkbox, CheckboxProps } from '../components';

export interface CheckboxItem {
  value: unknown;
  label: React.ReactNode;
  disabled?: boolean;
}

export interface CheckboxesProps {
  name: string;
  data: CheckboxItem | CheckboxItem[];
  label?: React.ReactNode;
  helperText?: string;
  required?: boolean;
  // it's a way to pass props to internal components
  fieldProps?: Partial<Parameters<typeof Field>[0]>;
  formControlLabelProps?: Partial<FormControlLabelProps>;
  formControlProps?: Partial<FormControlProps>;
  formGroupProps?: Partial<FormGroupProps>;
  formHelperTextProps?: Partial<FormHelperTextProps>;
  formLabelProps?: Partial<FormLabelProps>;
  checkboxProps?: Partial<CheckboxProps>;
}

interface CheckboxWrapperProps {
  name: string;
  data: CheckboxItem;
  isSingle: boolean;
  fieldProps: CheckboxesProps['fieldProps'];
  formControlLabelProps: CheckboxesProps['formControlLabelProps'];
  checkboxProps: CheckboxesProps['checkboxProps'];
}

const CheckboxWrapper: React.FC<CheckboxWrapperProps> = ({
  name,
  data,
  isSingle,
  fieldProps,
  formControlLabelProps,
  checkboxProps,
}) => {
  const { isEditable } = useCanEditFormFieldsContext();
  const disabled = data.disabled || !isEditable;

  return (
    <FormControlLabel
      label={data.label}
      name={name}
      disabled={disabled}
      value={isSingle ? undefined : data.value}
      control={
        <Field
          type='checkbox'
          name={name}
          render={(fieldProps) => {
            const {
              input: { name, value, onChange, checked },
            } = fieldProps;
            return (
              <Checkbox
                name={name}
                value={value}
                onChange={onChange}
                checked={checked}
                disabled={disabled}
                {...checkboxProps}
              />
            );
          }}
          {...fieldProps}
        />
      }
      {...formControlLabelProps}
    />
  );
};

export const Checkboxes: React.FC<CheckboxesProps> = (props) => {
  const {
    label,
    data,
    name,
    required,
    fieldProps,
    formControlLabelProps,
    formControlProps,
    formGroupProps,
    formHelperTextProps,
    formLabelProps,
    checkboxProps,
  } = props;
  const { meta } = useField(name);
  const isError = hasError(meta);

  const items = Array.isArray(data) ? data : [data];
  const isSingle = items.length === 1;
  const { error, submitError } = meta;
  const errorText = error || submitError;
  const helperText = isError ? errorText : props.helperText;

  return (
    <FormControl error={isError} required={required} {...formControlProps}>
      {label && <FormLabel {...formLabelProps}>{label}</FormLabel>}
      <FormGroup {...formGroupProps}>
        {items.map((item, index) => {
          return (
            <CheckboxWrapper
              key={index}
              name={name}
              data={item}
              isSingle={isSingle}
              fieldProps={fieldProps}
              formControlLabelProps={formControlLabelProps}
              checkboxProps={checkboxProps}
            />
          );
        })}
      </FormGroup>
      {helperText ? <FormHelperText {...formHelperTextProps}>{helperText}</FormHelperText> : null}
    </FormControl>
  );
};
